/**
 * English translation file
 */

export default {
  // Common
  common: {
    loading: 'Loading...',
    saving: 'Saving...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    search: 'Search',
    close: 'Close',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    error: 'Error',
    success: 'Success',
    unknown: 'Unknown'
  },

  // Navigation
  nav: {
    dashboard: 'Dashboard',
    pages: 'Saved Pages',
    subscription: 'Plan',
    settings: 'Settings',
    logout: 'Log Out',
    upgrade: 'Upgrade',
    freeUpgrade: 'Free → Upgrade'
  },

  // Header
  header: {
    tagline: 'Your Memory Guide',
    currentPlan: 'Current Plan',
    premiumUpgrade: 'Upgrade to Premium →'
  },

  // Dashboard
  dashboard: {
    greeting: 'Hello, {name}',
    greetingDefault: 'Hello, User',
    subtitle: 'What would you like to find today?',
    savedPages: 'Saved Pages',
    aiSearch: 'AI Search',
    plan: 'Plan',
    unit: 'pages',
    unitPerMonth: '/month',
    recentSaves: 'Recent Saves',
    viewAll: 'View All →',
    gettingStarted: 'Getting Started with KnoVelo',
    step1: 'Install the Chrome extension and start saving pages you find interesting',
    step2: 'Saved pages are automatically summarized and tagged by AI',
    step3: 'Ask Thoth on the right questions like "that red bridge page I saw yesterday"'
  },

  // Thoth Chatbot
  thoth: {
    name: 'Thoth',
    tagline: 'Your Memory Guide',
    welcome: `Hello! I'm Thoth, your memory guide.

Even vague memories like "that red bridge page I saw yesterday" or "the ramen shop I searched last week" are fine. I'll help you find it from your saved pages.

What are you looking for?`,
    placeholder: 'Fuzzy queries like "that red bridge" are OK',
    remainingSearch: 'Remaining',
    times: 'searches',
    suggestions: {
      yesterday: 'Pages from yesterday',
      lastWeekNews: 'Last week\'s news',
      techArticle: 'Tech articles',
      readLater: 'Read Later'
    },
    noPages: 'You haven\'t saved any pages yet. Save pages with the Chrome extension, and you\'ll be able to find them later even with vague memories!',
    limitReached: 'You\'ve reached the monthly AI search limit.',
    limitReachedPremium: 'Please wait for next month\'s reset.',
    limitReachedFree: 'Upgrade to Premium for up to 100 searches per month.',
    voiceInputStart: 'Voice input',
    voiceInputStop: 'Stop voice input',
    speak: 'Read aloud',
    stopSpeaking: 'Stop',
    feedbackThanks: 'Thanks for your feedback',
    feedbackQuestion: 'Was this helpful?',
    feedbackGood: 'Helpful',
    feedbackBad: 'Not helpful',
    disclaimer: 'Thoth is AI-powered and may not always be accurate.'
  },

  // Settings
  settings: {
    title: 'Settings',
    account: 'Account',
    plan: 'Plan',
    registeredDate: 'Registered',
    appSettings: 'App Settings',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark (Coming Soon)',
    notifications: 'Notifications',
    notificationsDesc: 'Receive browser notifications',
    saveSettings: 'Save Settings',
    settingsSaved: 'Settings saved',
    settingsSaveError: 'Failed to save settings',
    dataManagement: 'Data Management',
    dataExport: 'Export Data',
    dataExportDesc: 'Download saved pages in JSON format',
    exportBadge: 'VIP and above',
    logoutConfirm: 'Are you sure you want to log out?'
  },

  // Saved Pages
  pages: {
    title: 'Saved Pages',
    searchPlaceholder: 'Search by title or keyword...',
    noPages: 'No pages saved yet',
    noPagesDesc: 'Try saving a page with the Chrome extension',
    noResults: 'No results found',
    noResultsDesc: 'Try a different keyword',
    savedAt: 'Saved at',
    tags: 'Tags',
    summary: 'Summary',
    visitPage: 'Visit Page',
    deleteConfirm: 'Delete this page?',
    deleteSuccess: 'Page deleted',
    deleteError: 'Failed to delete page'
  },

  // Subscription
  subscription: {
    title: 'Choose Your Plan',
    subtitle: 'Enjoy KnoVelo with a plan that fits your needs',
    free: 'Free',
    pro: 'Pro',
    popular: 'Recommended',
    current: 'Current Plan',
    upgrade: 'Upgrade',
    upgrading: 'Processing...',
    comingSoon: 'Coming Soon',
    preparing: 'In Development',
    currentProPlan: 'You are currently on the Pro plan',
    manageSubscription: 'Manage Subscription',
    success: 'Thank you for your purchase! Your Pro plan is now active.',
    canceled: 'Checkout was canceled.',
    stripeNotConfigured: 'Stripe is not configured. Please contact support.',
    checkoutError: 'Failed to create checkout session',
    portalError: 'Failed to open subscription management page',
    pricePerMonth: '/month',
    freePrice: 'Free',
    features: {
      savedPages: 'Saved Pages',
      aiSearch: 'AI Search'
    },
    faq: {
      title: 'FAQ',
      cancel: {
        question: 'Can I cancel anytime?',
        answer: 'Yes, you can cancel anytime. After cancellation, you can continue using premium features until the end of your current billing period.'
      },
      payment: {
        question: 'What payment methods are accepted?',
        answer: 'We accept credit cards (Visa, Mastercard, American Express). Payments are processed securely through Stripe.'
      },
      downgrade: {
        question: 'What happens to my data if I downgrade?',
        answer: 'Your saved pages will not be deleted. However, if you exceed the Free plan limit (500 pages), you won\'t be able to save new pages.'
      }
    }
  },

  // Login
  login: {
    title: 'Welcome to KnoVelo',
    subtitle: 'Find what you\'re looking for with your memory guide',
    googleLogin: 'Sign in with Google',
    loginError: 'Login failed'
  },

  // Error messages
  errors: {
    generic: 'A temporary error occurred. Please try again later.',
    serverBusy: 'Server is busy. Please wait a moment and try again.',
    networkError: 'Network error. Please check your connection.'
  }
};
